/**
 * Função para decodificar entidades HTML
 * @param text - Texto com entidades HTML codificadas
 * @returns Texto com entidades HTML decodificadas
 */
export const decodeHtmlEntities = (text: string): string => {
  const textArea = document.createElement('textarea');
  textArea.innerHTML = text;
  return textArea.textContent || textArea.innerText || '';
};

/**
 * Remove tags HTML de uma string, retornando apenas o texto puro
 * @param html - String com HTML ou null/undefined
 * @returns Texto puro sem tags HTML
 */
export const stripHtml = (html: string | null | undefined): string => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

/**
 * Formata uma data (ISO ou MySQL) para o formato esperado pelo input datetime-local (YYYY-MM-DDTHH:mm)
 * @param dateString - String de data (ex: "2024-03-31 10:00:00" ou ISO)
 * @returns String formatada ou string vazia se inválida
 */
export const formatToDateTimeLocal = (dateString?: string): string => {
  if (!dateString) return "";
  try {
    // API returns "YYYY-MM-DD HH:mm:ss" or ISO. We need "YYYY-MM-DDTHH:mm" for input.
    const d = new Date(dateString.replace(" ", "T"));
    if (isNaN(d.getTime())) return "";

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (e) {
    return "";
  }
};

/**
 * Converte um caminho de imagem relativo em URL absoluta para o storage
 * @param path - Caminho da imagem (relativo ou URL completa)
 * @returns URL completa da imagem ou string vazia
 */
export const getImageUrl = (path: any): string => {
  if (!path || typeof path !== 'string') return "";
  if (path.startsWith("http")) return path;
  const baseUrl = import.meta.env.VITE_DO_SPACES_URL || "";
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

/**
 * Converte uma string datetime-local (YYYY-MM-DDTHH:mm) — interpretada como hora local do navegador
 * — em uma string ISO com fuso UTC, para envio correto ao backend.
 *
 * Ex: "2026-04-06T16:00" (hora de SP, UTC-3) → "2026-04-06T19:00:00.000Z" (UTC)
 *
 * @param dateTimeLocal - String no formato YYYY-MM-DDTHH:mm
 * @returns String ISO UTC ou null se inválida
 */
export const toUtcISOString = (dateTimeLocal?: string): string | null => {
  if (!dateTimeLocal) return null;
  try {
    const date = new Date(dateTimeLocal);
    if (isNaN(date.getTime())) return null;
    return date.toISOString();
  } catch {
    return null;
  }
};
/**
 * Limpa e formata um número de telefone para uso em links do WhatsApp (wa.me)
 * @param phone - Número de telefone em qualquer formato
 * @returns Número apenas com dígitos e DDI (55 padrão se necessário)
 */
export const formatPhoneForWhatsApp = (phone: string): string => {
  if (!phone || phone === 'N/A') return '';
  let cleanPhone = phone.replace(/\D/g, "");
  if (cleanPhone.startsWith('0')) cleanPhone = cleanPhone.substring(1);
  if (cleanPhone.length > 0 && cleanPhone.length <= 11) {
    cleanPhone = `55${cleanPhone}`;
  }
  return cleanPhone;
};

/**
 * Gera um slug amigável para URL a partir de uma string
 * @param text - Texto para converter em slug
 * @returns Slug formatado (ex: "nome-do-produto")
 */
export const slugify = (text: string): string => {
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD') // Decompõe caracteres acentuados
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .trim()
    .replace(/\s+/g, '-') // Substitui espaços por hífens
    .replace(/[^\w-]+/g, '') // Remove caracteres não alfanuméricos exceto hífens
    .replace(/--+/g, '-'); // Remove múltiplos hífens
};

/**
 * Abre uma conversa no WhatsApp para o telefone informado
 * @param phone - Número de telefone
 */
export const openWhatsAppChat = (phone: string): void => {
  const formattedPhone = formatPhoneForWhatsApp(phone);
  if (formattedPhone) {
    window.open(`https://wa.me/${formattedPhone}`, "_blank");
  }
};

/**
 * Formata uma data para o formato pt-BR com dia, mês, ano e hora (ex: DD/MM/AAAA às HH:MM)
 * @param dateStr - String de data
 * @returns String formatada ou "-" se inválida/ausente
 */
export const formatDateTime = (dateStr?: string): string => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  const date = d.toLocaleDateString('pt-BR');
  const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${date} às ${time}`;
};

/**
 * Comprime uma imagem nativamente usando o Canvas (Main Thread).
 * Ideal para evitar bugs do WebKit (iOS Safari) com WebWorkers.
 * @param file Arquivo de imagem original
 * @param maxWidth Largura/Altura máxima em pixels
 * @param quality Qualidade JPEG (0 a 1)
 * @returns Nova File comprimida
 */
export const compressImageNative = (file: File, maxWidth = 1920, quality = 0.7): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > maxWidth || height > maxWidth) {
          if (width > height) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else {
            width = Math.round((width * maxWidth) / height);
            height = maxWidth;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Failed to get canvas context'));

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Canvas to Blob failed'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
};

/**
 * Limita o valor de um input de porcentagem entre 0 e 100 com até duas casas decimais.
 * @param value String de entrada
 * @returns String limitada e formatada
 */
export const limitPercentage = (value: string): string => {
  // Permite apenas dígitos e ponto/vírgula
  let clean = value.replace(/[^0-9.,]/g, '').replace(',', '.');

  // Garante que só há um ponto decimal
  const parts = clean.split('.');
  if (parts.length > 2) {
    clean = parts[0] + '.' + parts.slice(1).join('');
  }

  if (clean === '') return '';

  const num = parseFloat(clean);
  if (isNaN(num)) return '';

  if (num > 100) return '100';
  if (num < 0) return '0';

  // Limita a duas casas decimais no valor digitado (ex: "12.345" -> "12.34")
  if (parts.length === 2 && parts[1].length > 2) {
    return `${parts[0]}.${parts[1].substring(0, 2)}`;
  }

  return clean;
};
